import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import moment from 'moment';
import { getBranches, getWaiterSalesReport } from '../../utilities/api';
import './billreports.css';

const VB2_BRANCH_FALLBACK = { branch_id: '2', branch_name: 'VB2' };

const findVb2Branch = (branchList) => {
    const vb2 = (branchList || []).find((b) => String(b?.branch_name || '').trim().toUpperCase() === 'VB2');
    return vb2 || VB2_BRANCH_FALLBACK;
};

const WaiterReport = () => {
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reportRows, setReportRows] = useState([]);
    const [reportColumns, setReportColumns] = useState({});

    const buildPayload = () => {
        const payload = {
            from_date: moment(fromDate).format('YYYY-MM-DD'),
            to_date: moment(toDate).format('YYYY-MM-DD'),
            branch_id: selectedBranch?.branch_id || VB2_BRANCH_FALLBACK.branch_id,
            client_id: localStorage.getItem('clientid') || ''
        };
        return payload;
    };

    const fetchWaiterReport = async () => {
        if (!fromDate || !toDate) return;
        setLoading(true);
        try {
            const res = await getWaiterSalesReport(buildPayload());
            if (res?.status === 'success') {
                setReportColumns(res?.columns && typeof res.columns === 'object' ? res.columns : {});
                setReportRows(Array.isArray(res?.rows) ? res.rows : []);
            } else {
                setReportColumns({});
                setReportRows([]);
            }
        } catch (error) {
            console.error('Error loading waiter report:', error);
            setReportColumns({});
            setReportRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            const clientId = localStorage.getItem('clientid');
            if (clientId) {
                try {
                    const branchList = await getBranches(clientId);
                    const vb2 = findVb2Branch(branchList);
                    setBranches([vb2]);
                    setSelectedBranch(vb2);
                } catch (e) {
                    console.error('Failed to load branches:', e);
                    setBranches([VB2_BRANCH_FALLBACK]);
                    setSelectedBranch(VB2_BRANCH_FALLBACK);
                }
            } else {
                setBranches([VB2_BRANCH_FALLBACK]);
                setSelectedBranch(VB2_BRANCH_FALLBACK);
            }
        };
        init();
    }, []);

    const tableColumns = useMemo(() => {
        const cols = Object.entries(reportColumns || {});
        return cols;
    }, [reportColumns]);

    return (
        <div className="bill-reports-container">
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Waiter Report
                </h2>
                <p className="text-color-secondary">View waiter performance by hourly time slots</p>
            </div>

            <Card className="mb-4 bill-reports-filter-card">
                <div className="flex flex-wrap gap-4 align-items-end bill-reports-filter-row">
                    <div className="flex flex-column date-range-block" style={{ flex: '2 1 420px', minWidth: '420px' }}>
                        <label className="mb-2 font-semibold text-lg" style={{ color: 'var(--primary-color)' }}>
                            Select Date Range
                        </label>
                        <div className="flex gap-3 date-range-row">
                            <div className="flex flex-column date-field" style={{ flex: '1' }}>
                                <label className="mb-2 font-medium text-sm">
                                    From Date <span className="text-red-500">*</span>
                                </label>
                                <Calendar value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon className="w-full" showButtonBar placeholder="From Date" />
                            </div>
                            <div className="flex flex-column date-field" style={{ flex: '1' }}>
                                <label className="mb-2 font-medium text-sm">
                                    To Date <span className="text-red-500">*</span>
                                </label>
                                <Calendar value={toDate} onChange={(e) => setToDate(e.value)} minDate={fromDate} dateFormat="dd-mm-yy" showIcon className="w-full" showButtonBar placeholder="To Date" />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '200px' }}>
                        <label className="mb-2 font-medium text-sm">Branch</label>
                        <Dropdown value={selectedBranch} options={branches} onChange={(e) => setSelectedBranch(e.value)} optionLabel="branch_name" placeholder="VB2" className="w-full" disabled />
                    </div>

                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Apply Filters" icon="pi pi-check" className="p-button-primary" onClick={fetchWaiterReport} disabled={!fromDate || !toDate || !selectedBranch || loading} loading={loading} />
                    </div>
                </div>
            </Card>

            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Waiter Report Data</h3>
                    {reportRows.length > 0 && <Tag value={`${reportRows.length} Records`} severity="info" />}
                </div>
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : reportRows.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6">
                        <i className="pi pi-users text-6xl text-color-secondary mb-3"></i>
                        <p className="text-xl text-color-secondary">No waiter data</p>
                        <p className="text-color-secondary mt-2">No waiter sales found for selected filters.</p>
                    </div>
                ) : (
                    <DataTable
                        value={reportRows}
                        dataKey="waiter_no"
                        showGridlines
                        paginator
                        rows={25}
                        stripedRows
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        tableStyle={{ minWidth: '50rem' }}
                        className="p-datatable-sm"
                        emptyMessage="No waiter report rows"
                    >
                        <Column field="waiter_no" header="Waiter No" sortable style={{ minWidth: '110px' }} />
                        {tableColumns.map(([field, header]) => (
                            <Column key={field} field={field} header={header} style={{ minWidth: '130px', textAlign: 'right' }} />
                        ))}
                    </DataTable>
                )}
            </Card>
        </div>
    );
};

export default WaiterReport;
